import { Ref } from 'vue'

export type WizardState = {
    previous: WizardData[]
    current: WizardData | null
    currentIndex: number | null
    backButtonFocused: boolean
}

export interface WizardInterface {
    data: Ref<WizardState>
    focusedHasChildren: () => boolean
    previousExists: () => boolean
    lastFocused: () => boolean
    firstFocused: () => boolean
    someItemIsFocused: () => boolean
    focusFirstItem: () => void
    focusLastItem: () => void
    setCurrentIndex: (index: number) => void
    setBackButtonFocusState: (focus: boolean) => boolean
    focusNext: () => void
    focusPrevious: () => void
    rememberLastFocused: () => void
    addCurrentToPrevious: () => number
    resetCurrentIndex: () => null
    switchToNext: (nextData: WizardData) => void
    setCurrent: (newCurrent: WizardData) => WizardData
    goBack: (autofocus: boolean) => void
    reset: () => void
}

export const useWizard = (): WizardInterface => {
    // default state
    const data = useState<WizardState>('wizard', () => ({
        previous: [],
        current: null,
        currentIndex: null,
        backButtonFocused: false,
    }))

    // determine if focused item has children
    const focusedHasChildren = (): boolean =>
        Boolean(
            data.value.current?.children?.[data.value.currentIndex as number]
                ?.children?.length
        )

    // determine if previous collections exists
    const previousExists = (): boolean => data.value.previous.length > 0

    // focus last item from current collection
    const lastFocused = (): boolean =>
        data.value.currentIndex ===
        (data.value.current?.children?.length as number) - 1

    // determine if first item from current collection is focused
    const firstFocused = (): boolean => data.value.currentIndex === 0

    // focus last item from current collection
    const focusLastItem = (): void => {
        if (data.value.current?.children) {
            data.value.currentIndex = data.value.current?.children?.length - 1
        }
    }

    // determine if any item from current collection is focused
    const someItemIsFocused = (): boolean => data.value.currentIndex !== null

    // set current index for focus right item
    const setCurrentIndex = (index: number | null): void => {
        data.value.currentIndex = index
    }

    // focus firt item from current collection
    const focusFirstItem = (): number => (data.value.currentIndex = 0)

    // focus/unfocus back button
    const setBackButtonFocusState = (focus: boolean): boolean =>
        (data.value.backButtonFocused = focus)

    // focus next item from current collection
    const focusNext = (): void => {
        if (data.value.currentIndex !== null) {
            data.value.currentIndex += 1
        }
    }

    // focus previous item from current collection
    const focusPrevious = (): void => {
        if (data.value.currentIndex !== null) {
            data.value.currentIndex -= 1
        }
    }

    // remember which item from current collection has been focused as last
    const rememberLastFocused = (): void => {
        if (data.value.current) {
            // remember last focused from current collection
            data.value.current.lastSelected = data.value.currentIndex
        }
    }

    // set current collection
    const setCurrent = (newCurrent: WizardData): WizardData =>
        (data.value.current = newCurrent)

    // add current collection to previous for return
    const addCurrentToPrevious = (): number =>
        data.value.previous.push(data.value.current as WizardData)

    const switchToNext = (nextData: WizardData): void => {
        addCurrentToPrevious()
        setCurrent(nextData)
        resetCurrentIndex()
    }

    // reset current index to null
    const resetCurrentIndex = (): null => (data.value.currentIndex = null)

    // show previous
    const goBack = (autofocus: boolean): void => {
        setCurrent(data.value.previous.pop() as WizardData)
        if (autofocus) {
            setTimeout(() => {
                const lastIndex = data.value.current?.lastSelected
                setCurrentIndex(lastIndex !== undefined ? lastIndex : null)
            }, 1)
        }
    }

    // reset wizard state
    const reset = (): void => {
        data.value.current = null
        data.value.previous = []
        data.value.currentIndex = null
        data.value.backButtonFocused = false
    }

    return {
        data,
        focusedHasChildren,
        previousExists,
        lastFocused,
        firstFocused,
        someItemIsFocused,
        focusFirstItem,
        focusLastItem,
        setCurrentIndex,
        setBackButtonFocusState,
        focusNext,
        focusPrevious,
        rememberLastFocused,
        addCurrentToPrevious,
        switchToNext,
        resetCurrentIndex,
        setCurrent,
        goBack,
        reset,
    }
}
